import { buildSetupSteps, type SetupStep } from '../components/SetupChecklist.js';
import { useAuth } from './store.js';
import {
  useApprovals, useCurrentRun, usePosts, useProject, useProjects, useConnectors, useAgents, useReadiness,
  type ActiveRun, type AgentRunView, type PostView, type ProjectSummary, type RunPhase,
} from './queries.js';
import { when, statusLabel } from './format.js';
import { t } from './i18n.js';

/**
 * Abgeleitete Sichten für das Gerüst (README §6.1–6.3, §7).
 *
 * Nichts hier fragt eigene Daten ab, alles kommt aus den bestehenden Hooks.
 * Die Pipeline-Leiste, die Aktivitätsleiste und „Heute für dich" lesen
 * dieselben Quellen wie die Seiten -- was dort steht, stimmt also mit dem
 * überein, was man eine Ebene tiefer sieht.
 */

export type StageState = 'done' | 'live' | 'you' | 'idle';
export type StageKey = 'create' | 'review' | 'render' | 'publish' | 'learn';

export interface Stage {
  key: StageKey;
  n: number;
  label: string;
  state: StageState;
  sub: string;
  /** Unterseite im Projekt, zu der ein Klick führt */
  to: string;
  /** Wie viel hier auf den Nutzer wartet (offene Freigaben); als kleiner Zähler am Kreis */
  waiting?: number;
}


/** Fertige und geplante Medien eines Laufs, aus den Schritten der Jobs. */
export function mediaCount(run: AgentRunView | null | undefined): { done: number; total: number } {
  const jobs = run?.jobs ?? [];
  const total = jobs.length;
  const done = jobs.filter((j) => j.steps.find((s) => s.key === 'media')?.state === 'done').length;
  return { done, total };
}

/** Die Phase eines laufenden Laufs in Worten. */
export function phaseLabel(phase: RunPhase | undefined): string {
  switch (phase) {
    case 'queued': return t('wartet auf einen freien Platz');
    case 'estimate': return t('schätzt die Kosten');
    case 'plan': return t('schreibt Hooks und Texte');
    case 'media': return t('erzeugt Bilder');
    case 'music': return t('wählt Musik');
    case 'store': return t('legt Entwürfe ab');
    case 'done': return t('fertig');
    default: return t('arbeitet');
  }
}

/** „heute 18:30" für den nächsten geplanten Post, oder null. */
function nextPostWhen(posts: PostView[] | undefined): string | null {
  const now = Date.now();
  const next = (posts ?? [])
    .filter((p) => p.scheduledAt && new Date(p.scheduledAt).getTime() > now)
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime())[0];
  return next ? when(next.scheduledAt) : null;
}

export function usePipeline(projectId: string | undefined): { stages: Stage[]; active: ActiveRun | null; run: AgentRunView | null } {
  const { data: project } = useProject(projectId);
  const { data: current } = useCurrentRun(projectId);
  const { data: agents } = useAgents(projectId);
  const { data: busy } = usePosts({ projectId, status: 'rendering,publishing', limit: 50 });
  const { data: planned } = usePosts({ projectId, status: 'approved,scheduled', limit: 20 });

  const active = current?.active?.[0] ?? null;
  const run = current?.run ?? null;
  const phase: RunPhase | undefined = active ? (active.phase ?? 'queued') : undefined;
  const recipe = agents?.find((agent) => agent.id === (active?.agentId ?? run?.agentId));
  const base = projectId ? `/projects/${projectId}` : '';
  const pending = project?.stats?.pendingApproval ?? 0;
  const rendering = (busy ?? []).filter((p) => p.status === 'rendering').length;
  const publishing = (busy ?? []).filter((p) => p.status === 'publishing').length;
  const media = mediaCount(run);
  const learnings = project?.learnings?.length ?? 0;
  const next = nextPostWhen(planned);

  const createLive = !!phase && phase !== 'done';
  const storyWaiting = run?.status === 'paused' && run.awaitingStoryReview;

  const stages: Stage[] = [
    {
      key: 'create', n: 1, label: t('Planen & Erstellen'),
      state: createLive ? 'live' : storyWaiting ? 'you' : run?.status === 'done' ? 'done' : run?.status === 'failed' ? 'you' : 'idle',
      sub: createLive
        ? `${['queued', 'estimate', 'plan'].includes(phase!) ? t('Planen · {phase}', { phase: phaseLabel(phase) }) : t('Erstellen · {phase}', { phase: phaseLabel(phase) })}${recipe ? ` · ${recipe.name}` : ''}`
        : storyWaiting ? t('Story wartet auf deine Freigabe')
          : run?.status === 'paused' ? t('pausiert')
          : run?.status === 'done' ? `${t('{done}/{total} Medien', media)}${recipe ? ` · ${recipe.name}` : ''}`
          : run?.status === 'failed' ? t('letzter Lauf fehlgeschlagen')
            : run?.status === 'aborted' ? t('letzter Lauf abgebrochen') : t('noch kein Lauf'),
      to: `${base}/run`,
    },
    {
      key: 'review', n: 2, label: t('Prüfen'),
      // Ohne einen einzigen fertigen Lauf gibt es nichts, das „geprüft" sein könnte.
      state: pending > 0 ? 'you' : run?.status === 'done' ? 'done' : 'idle',
      sub: pending > 0 ? t('{n} warten auf dich', { n: pending })
          : run?.status === 'done' ? t('alles geprüft') : createLive ? t('Entwürfe kommen gleich') : t('noch nichts zu prüfen'),
      to: `${base}/review`,
      waiting: pending,
    },
    {
      key: 'render', n: 3, label: t('Rendern'),
      state: rendering > 0 ? 'live' : 'idle',
      sub: rendering > 0 ? t('{n} rendert', { n: rendering }) : t('nichts in Arbeit'),
      to: `${base}/publish`,
    },
    {
      key: 'publish', n: 4, label: t('Veröffentlichen'),
      state: publishing > 0 ? 'live' : 'idle',
      sub: publishing > 0 ? t('postet gerade…') : next ? t('nächster {when}', { when: next }) : t('nichts geplant'),
      to: `${base}/publish`,
    },
    {
      key: 'learn', n: 5, label: t('Lernen'),
      state: learnings > 0 ? 'done' : 'idle',
      sub: learnings > 0 ? t('{n} Regeln', { n: learnings }) : t('lernt aus deinen Entscheidungen'),
      to: `${base}/results#lernen`,
    },
  ];

  return { stages, active, run };
}

/**
 * Fünf Balken für die Projektkarte auf „Alle Projekte" -- nur aus der
 * Projektzusammenfassung, ohne je Projekt nachzuladen.
 */
export function miniPipeline(p: ProjectSummary): StageState[] {
  const live = !!p.activeRun;
  const pending = p.stats?.pendingApproval ?? 0;
  return [
    live ? 'live' : p.lastRunAt ? 'done' : 'idle',
    pending > 0 ? 'you' : p.lastRunAt ? 'done' : 'idle',
    (p.stats?.queueCount ?? 0) > 0 ? 'done' : 'idle',
    (p.stats?.publishedCount ?? 0) > 0 ? 'done' : 'idle',
    (p.learnings?.length ?? 0) > 0 ? 'done' : 'idle',
  ];
}

/* ────────────────────────────────────────────── Heute für dich (Konto) */

export type Tone = 'warn' | 'accent' | 'err' | 'ok';

export interface TodayItem {
  key: string;
  tone: Tone;
  text: string;
  project: string;
  projectId: string;
  cta: string;
  to: string;
}

export function useTodayItems(): TodayItem[] {
  const { data: projects } = useProjects();
  const { data: approvals } = useApprovals();
  const items: TodayItem[] = [];

  for (const g of approvals?.groups ?? []) {
    if (!g.count) continue;
    items.push({
      key: `review-${g.projectId}`, tone: 'warn',
      text: g.count === 1 ? t('1 Beitrag wartet auf deine Freigabe') : t('{n} Beiträge warten auf deine Freigabe', { n: g.count }),
      project: g.name, projectId: g.projectId, cta: t('Prüfen'), to: `/projects/${g.projectId}/review`,
    });
  }
  for (const p of projects ?? []) {
    if (p.activeRun) {
      items.push({
        key: `run-${p.id}`, tone: 'accent',
        text: t('Der Agent baut gerade · {p} %', { p: p.activeRun.progress }),
        project: p.name, projectId: p.id, cta: t('Ansehen'), to: `/projects/${p.id}/run`,
      });
    }
    for (const a of p.accounts ?? []) {
      if (a.connected && a.tokenHealth !== 'ok') {
        items.push({
          key: `token-${a.id}`, tone: 'warn',
          text: a.tokenHealth === 'expired'
            ? t('{platform}-Zugang ist abgelaufen', { platform: a.platform === 'tiktok' ? 'TikTok' : 'Instagram' })
            : t('{platform}-Zugang läuft bald ab', { platform: a.platform === 'tiktok' ? 'TikTok' : 'Instagram' }),
          project: p.name, projectId: p.id, cta: t('Erneuern'), to: `/projects/${p.id}/channels`,
        });
      }
    }
    const failed = p.health?.find((h) => h.key === 'failed');
    if (failed) {
      items.push({
        key: `failed-${p.id}`, tone: 'err',
        text: t('Beiträge sind fehlgeschlagen und brauchen einen Blick'),
        project: p.name, projectId: p.id, cta: t('Ansehen'), to: `/projects/${p.id}/publish?view=list`,
      });
    }
    if (p.accounts && !p.accounts.some((a) => a.connected)) {
      items.push({
        key: `connect-${p.id}`, tone: 'err',
        text: t('Kein Kanal verbunden, es kann nichts veröffentlicht werden'),
        project: p.name, projectId: p.id, cta: t('Verbinden'), to: `/projects/${p.id}/channels`,
      });
    }
  }
  return items;
}

/* ────────────────────────────────────────────────── Aktivitätsleiste */

export interface ActivityEntry {
  key: string;
  tone: 'live' | 'ok' | 'err' | 'you';
  glyph: string;
  title: string;
  meta: string;
  progress?: number;
  link?: { label: string; to: string };
  /** Beitrag, der bei einem Fehler nur für den fehlgeschlagenen Kanal neu gesendet werden kann */
  retryPostId?: string;
}

export function useActivity(): { now: ActivityEntry[]; next: ActivityEntry[]; recent: ActivityEntry[] } {
  const { data: projects } = useProjects();
  const { data: approvals } = useApprovals();
  const { data: recent } = usePosts({ sort: 'recent', limit: 30 });
  const nameOf = new Map((projects ?? []).map((p) => [p.id, p.name]));

  const now: ActivityEntry[] = [];
  for (const p of projects ?? []) {
    if (p.activeRun) {
      now.push({
        key: `run-${p.id}`, tone: 'live', glyph: '↻',
        title: t('Lauf in {name}', { name: p.name }),
        meta: t('{p} % fertig', { p: p.activeRun.progress }),
        progress: p.activeRun.progress,
        link: { label: t('Live ansehen →'), to: `/projects/${p.id}/run` },
      });
    }
  }
  for (const post of recent ?? []) {
    if (post.status === 'rendering' || post.status === 'generating' || post.status === 'publishing') {
      now.push({
        key: `busy-${post.id}`, tone: 'live', glyph: '◌',
        title: post.hook || t('Beitrag'),
        meta: `${statusLabel(post.status)} · ${nameOf.get(post.projectId) ?? ''}`,
      });
    }
  }

  const next: ActivityEntry[] = [];
  for (const g of approvals?.groups ?? []) {
    if (!g.count) continue;
    next.push({
      key: `review-${g.projectId}`, tone: 'you', glyph: '!',
      title: g.count === 1 ? t('1 Entwurf wartet') : t('{n} Entwürfe warten', { n: g.count }),
      meta: g.name,
      link: { label: t('Jetzt prüfen →'), to: `/projects/${g.projectId}/review` },
    });
  }
  const upcoming = (recent ?? [])
    .filter((p) => (p.status === 'scheduled' || p.status === 'approved') && p.scheduledAt && new Date(p.scheduledAt).getTime() > Date.now())
    .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime())[0];
  if (upcoming) {
    next.push({
      key: `next-${upcoming.id}`, tone: 'live', glyph: '›',
      title: upcoming.hook || t('Nächster Beitrag'),
      meta: `${when(upcoming.scheduledAt)} · ${nameOf.get(upcoming.projectId) ?? ''}`,
      link: { label: t('Kalender →'), to: `/projects/${upcoming.projectId}/publish` },
    });
  }

  const done: ActivityEntry[] = [];
  for (const post of recent ?? []) {
    if (post.status === 'published') {
      done.push({
        key: `pub-${post.id}`, tone: 'ok', glyph: '✓',
        title: post.hook || t('Beitrag'),
        meta: `${t('Veröffentlicht')} · ${nameOf.get(post.projectId) ?? ''}`,
      });
    } else if (post.status === 'failed') {
      const failedTargets = post.results.filter((r) => r.state === 'failed').map((r) => r.target);
      const partial = post.results.some((r) => r.state === 'ok' || r.state === 'draft_on_platform');
      done.push({
        key: `fail-${post.id}`, tone: 'err', glyph: '✕',
        title: post.hook || t('Beitrag'),
        meta: post.error
          ? post.error.slice(0, 90)
          : `${t('Fehlgeschlagen')} · ${nameOf.get(post.projectId) ?? ''}`,
        ...(partial && failedTargets.length ? { retryPostId: post.id } : {}),
        link: { label: post.scenes.some((s) => s.url) ? t('Ansehen →') : t('Neu bauen →'), to: `/projects/${post.projectId}/publish?post=${post.id}` },
      });
    }
    if (done.length >= 8) break;
  }
  for (const p of projects ?? []) {
    if ((p.learnings?.length ?? 0) > 0) {
      done.push({
        key: `learn-${p.id}`, tone: 'ok', glyph: '✦',
        title: t('{n} Lernregeln aktiv', { n: p.learnings.length }),
        meta: p.name,
      });
    }
  }

  return { now, next, recent: done };
}

/* ─────────────────────────────────────────────────── Einrichtung */

/**
 * Die Einrichtungsschritte eines Projekts mit allen Zutaten, an EINER
 * Stelle berechnet: Seitenleiste („Einrichten 5/7"), Cockpit-Hero und die
 * Seite „Einrichten" lesen dasselbe.
 */
export function useSetupSteps(projectId: string | undefined): {
  steps: SetupStep[];
  done: number;
  total: number;
  /** Erster offener Pflichtschritt, sonst null */
  next: SetupStep | null;
  ready: boolean;
} {
  const { data: project } = useProject(projectId);
  const { data: connectors } = useConnectors();
  const { data: agents } = useAgents(projectId);
  const { data: readiness } = useReadiness(projectId);
  const { data: current } = useCurrentRun(projectId);
  const org = useAuth((s) => s.org);
  const user = useAuth((s) => s.user);

  if (!project) return { steps: [], done: 0, total: 0, next: null, ready: false };
  const steps = buildSetupSteps(project, {
    hasAgent: (agents?.length ?? 0) > 0,
    /*
     * Dieselbe Antwort wie die Vorprüfung vor jedem Lauf: Ist ein
     * Bildanbieter wirklich erreichbar? Vorher reichte ein angelegter
     * Connector, und die Einrichtung hakte ab, was der Lauf dann ablehnte.
     */
    hasConnector: readiness
      ? readiness.image.ok && !readiness.image.mocked
      : (connectors ?? []).some((c) => c.hasKey || c.managed),
    hasPosts: (project.stats?.queueCount ?? 0) > 0 || (project.stats?.publishedCount ?? 0) > 0,
    runActive: (current?.active?.length ?? 0) > 0,
    creditsOk: !!user?.unlimited || (org?.available ?? 0) > 0,
  });
  const required = steps.filter((s) => !s.optional);
  return {
    steps,
    done: steps.filter((s) => s.done).length,
    total: steps.length,
    next: required.find((s) => !s.done) ?? null,
    ready: true,
  };
}
